import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Chip } from '@/components/forge/Chip'
import { Field } from '@/components/forge/Field'
import { ForgeButton } from '@/components/forge/ForgeButton'
import { ForgeDrawer } from '@/components/forge/ForgeDrawer'
// import { ForgeSelect } from '@/components/forge/ForgeSelect'  // the grace picker only
import { Icon } from '@/components/forge/Icon'
import { PlaceField } from '@/components/forge/PlaceField'
import { Eyebrow, Num } from '@/components/forge/primitives'
// `TINT_CLASS` went with the tone swatches; `ON_TINT_CLASS` tints a type chip's icon.
import { ON_TINT_CLASS } from '@/lib/tone'
// import { cn } from '@/lib/cn'  // used only by the commented-out tone swatches
import { SHIFT_TYPES, lookFor } from '@/features/shiftPatterns/api'
import {
  DOW_ORDER,
  crossesMidnight,
  overlappingPatterns,
  patternDuration,
} from '@/features/shiftPatterns/arithmetic'
// Gone with the fields they fed. `GRACE_OPTIONS`, `PATTERN_ICONS`,
// `PATTERN_PRESETS` and `PATTERN_TONES` are commented out in arithmetic.js;
// `SHIFT_SITES`/`SITE_TIME_ZONE` are the real hierarchy now: the plant is chosen
// through `PlaceField`, and the `sites` prop only names one. See below.

/**
 * Add or edit one shift pattern.
 *
 * LIVE. Four sections, as the design has them: Identity, Timing, Days
 * Applicable, Scope & Detection — then the conflict notice and the validation
 * notice above a Delete / Cancel / Save footer.
 *
 * ── A drawer, not a dialog ────────────────────────────────────────────────
 * The design draws it as a 520px panel over the pattern grid, and the reason it
 * is right is that the overlap warning is about the OTHER cards: you edit a
 * window while looking at the ones it might collide with. `ForgeDrawer` is the
 * repo's object for exactly that and brings the focus trap with it.
 *
 * ── The warnings are computed, not decorative ─────────────────────────────
 * The overlap notice re-runs against the live pattern list on every keystroke,
 * so it appears and clears as the times move. It is the ONLY thing checking for
 * overlaps — the server stores two patterns claiming one minute without
 * complaint — so it warns and still allows the save, because an overlap is
 * occasionally deliberate and the resolution rule is defined.
 *
 * The mismatch notice moved from the name to the TYPE. The demo compared a
 * pattern called "Night" against a client-side preset list; `type` is a stored
 * enum, so a pattern typed `night` running 06:00–14:00 is now a check against a
 * real field rather than against a string somebody happened to type. Same
 * failure caught — a shift that lies about its window is how a login gets
 * attributed to hours nobody worked.
 *
 * ── Three controls are commented out below, not deleted ──────────────────
 * The icon picker, the tone swatches and the grace select. The first two are
 * replaced by `type`, which the API does store and which decides the look
 * deterministically — see `TYPE_LOOK`. Grace has no field at all and is the one
 * that costs something real: it is the whole auto-detection story the design's
 * banner tells. Recorded in BACKEND-GAPS.md.
 */
/**
 * Hours a type is normally worked, for the mismatch check only.
 *
 * Not defaults and not validation — nothing here fills a field or blocks a save.
 * It exists to ask one question: does this pattern's window resemble what its
 * type claims? A night shift that starts at six in the morning is either a typo
 * or a type nobody updated, and both are worth a sentence.
 *
 * `general` and `rotating` are absent deliberately. Neither names a time of day,
 * so neither can contradict one.
 */
const TYPICAL_START_HOUR = { day: [5, 12], morning: [4, 10], evening: [12, 19], night: [19, 4] }

/** Whether an `HH:MM` falls in a window that may wrap past midnight. */
function startsWithin(clock, [from, to]) {
  const hour = Number((clock || '').slice(0, 2))
  return from <= to ? hour >= from && hour < to : hour >= from || hour < to
}

export function ShiftPatternDrawer({
  open,
  pattern,
  patterns,
  sites,
  grant,
  saving,
  onClose,
  onSave,
  onDelete,
  onToggleActive,
}) {
  const { t } = useTranslation(['app', 'common'])
  const [form, setForm] = useState(pattern)

  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }))

  const siteName = new Map(sites.map((site) => [site.id, site.name]))

  /* "Every site" or "one site". Held apart from `plantId` because choosing
     "one site" before a plant has been picked is a real, unfinished state —
     `plantId` alone would read it as "every site". A plant-level grant starts on
     "one site", since "every site" is not theirs to create. */
  const [oneSite, setOneSite] = useState(pattern.plantId !== null || !grant.accountLevel)
  /* The name of a plant picked in the dialog, which may lie beyond the page of
     plants the screen read for its cards. */
  const [pickedName, setPickedName] = useState(null)
  const chosenPlant =
    form.plantId === null
      ? null
      : { id: form.plantId, tier: 'site', name: pickedName ?? siteName.get(form.plantId) ?? form.plantId }
  /* The picker shows every plant the reader can see; a plant-level grant may
     only CREATE at the plants it holds. Checked on the pick, said beside the
     control, rather than discovered as the server's refusal. */
  const plantNotGranted =
    form.plantId !== null && !grant.accountLevel && !grant.plantIds.has(form.plantId)

  /* Whether anything on the form has been touched since it opened.
     The status act applies to the STORED pattern and closes the drawer, exactly
     as Delete does — so a half-typed rename would go with it. The confirmation
     says so when there is something to lose, and says nothing when there is
     not. Compared field by field rather than by reference: `form` is a fresh
     object from the first keystroke. */
  const dirty =
    form.name !== pattern.name ||
    form.type !== pattern.type ||
    form.start !== pattern.start ||
    form.end !== pattern.end ||
    form.plantId !== pattern.plantId ||
    form.days.length !== pattern.days.length ||
    form.days.some((day) => !pattern.days.includes(day))

  const isNew = !form.id
  const duration = patternDuration(form.start || '00:00', form.end || '00:00')
  const wraps = Boolean(form.start && form.end && crossesMidnight(form.start, form.end))
  const conflicts = form.start && form.end ? overlappingPatterns(form, patterns) : []

  /* A pattern typed `night` that does not run night hours. `type` is what every
     other screen reads to decide what this shift IS, so a type that disagrees
     with the window is how a login gets attributed to hours nobody worked. */
  const typical = TYPICAL_START_HOUR[form.type]
  const mismatch = Boolean(form.start && typical && !startsWithin(form.start, typical))

  const error = !form.name.trim()
    ? t('shifts.master.error.name')
    : !form.start || !form.end
      ? t('shifts.master.error.times')
      : form.start === form.end
        ? t('shifts.master.error.identical')
        : !form.days.length
          ? /* The server says so too — `week_days` has `min_length=1` — but the
               answer belongs beside the control rather than in a banner. */
            t('shifts.master.error.days')
          : form.plantId === null && (oneSite || !grant.accountLevel)
            ? /* "One site" with none picked yet — or an account-level pattern
                 without the account-level grant it needs, which the server
                 answers with a 403 about the caller rather than the field. */
              t('shifts.master.error.site')
            : isNew && plantNotGranted
              ? t('shifts.master.error.siteNotGranted')
              : null

  return (
    <ForgeDrawer
      open={open}
      onOpenChange={(next) => !next && onClose()}
      className="w-[min(33rem,100vw)]"
      title={isNew ? t('shifts.master.drawer.addTitle') : t('shifts.master.drawer.editTitle')}
      closeLabel={t('common:actions.close')}
      footer={
        <>
          {isNew ? null : (
            <>
              <ForgeButton variant="danger" onClick={() => onDelete(form.id)}>
                {t('common:actions.delete')}
              </ForgeButton>
              {/* Beside Delete because it is the same kind of thing: an act on
                  the pattern rather than a change to it. Both confirm, both
                  apply on their own, and neither waits for Save. */}
              <ForgeButton onClick={() => onToggleActive(dirty)}>
                {t(form.active ? 'shifts.master.deactivate' : 'shifts.master.activate')}
              </ForgeButton>
            </>
          )}
          <div className="flex-1" />
          <ForgeButton onClick={onClose}>{t('common:actions.cancel')}</ForgeButton>
          <ForgeButton
            variant="primary"
            disabled={Boolean(error) || saving}
            onClick={() => onSave(form)}
          >
            {saving ? t('shifts.master.drawer.saving') : t('shifts.master.drawer.save')}
          </ForgeButton>
        </>
      }
    >
      <p className="mt-0 mb-4 text-body-sm text-ink-dim">{t('shifts.master.drawer.lead')}</p>

      <Section icon="cal" title={t('shifts.master.identity')} sub={t('shifts.master.identitySub')}>
        {/* The preset chips are commented out with the list behind them. They
            set a name, an icon, a tone and both times in one press; of those
            four, two no longer exist as fields and the times are what the type
            select now implies rather than fills.
            <div className="mb-3 flex flex-wrap gap-1.5">
              {PATTERN_PRESETS.map((entry) => (
                <Chip …>{entry.name}</Chip>
              ))}
            </div> */}

        <Field
          label={t('shifts.master.field.name')}
          required
          value={form.name}
          maxLength={150}
          onChange={(event) => set('name', event.target.value)}
          placeholder={t('shifts.master.field.namePlaceholder')}
        />

        {/* The type, which is a stored field and decides the look. Chips rather
            than a select because there are six, each carries its own icon, and
            the point of the control is seeing what the card will become. */}
        <Eyebrow as="p" className="mt-4 mb-2">
          {t('shifts.master.field.type')}
        </Eyebrow>
        <div className="flex flex-wrap gap-1.5">
          {SHIFT_TYPES.map((type) => {
            const look = lookFor(type)
            return (
              <Chip key={type} selected={form.type === type} onClick={() => set('type', type)}>
                <Icon
                  name={look.icon}
                  size="xs"
                  className={form.type === type ? undefined : ON_TINT_CLASS[look.tone]}
                />
                {t(`shifts.master.type.${type}`)}
              </Chip>
            )
          })}
        </div>

        {/* The icon and tone pickers are commented out, not deleted. `type`
            decides both through `TYPE_LOOK`, which is better than the design:
            a night shift cannot be given the day shift's colour by accident.
            Restoring them means an `icon` and a `colour` column on the server —
            and the colour would have to be a tone id, never the prototype's
            `col: "#F59E0B"`, which cannot survive `data-theme` or colour-blind
            mode.
            <Eyebrow as="p">{t('shifts.master.field.icon')}</Eyebrow>
            {PATTERN_ICONS.map((name) => <Chip …><Icon name={name} /></Chip>)}
            <Eyebrow as="p">{t('shifts.master.field.tone')}</Eyebrow>
            {PATTERN_TONES.map((tone) => <button … className={TINT_CLASS[tone]} />)} */}

        {mismatch ? (
          <p className="mt-3 mb-0 text-body-sm text-on-amber">
            {t('shifts.master.mismatch', {
              type: t(`shifts.master.type.${form.type}`),
              start: form.start,
            })}
          </p>
        ) : null}
      </Section>

      <Section icon="gauge" title={t('shifts.master.timing')} sub={t('shifts.master.timingSub')}>
        <div className="grid grid-cols-2 gap-3">
          <Field
            type="time"
            label={t('shifts.master.field.start')}
            required
            value={form.start}
            onChange={(event) => set('start', event.target.value)}
          />
          <Field
            type="time"
            label={t('shifts.master.field.end')}
            required
            value={form.end}
            onChange={(event) => set('end', event.target.value)}
          />
        </div>

        <div className="mt-3 flex items-center gap-3 rounded-sm border border-hairline bg-sunken px-3.5 py-3">
          <div className="min-w-0 flex-1">
            <Eyebrow as="p" className="mb-1">
              {t('shifts.master.duration')}
            </Eyebrow>
            <Num className="text-metric-sm">
              {form.start && form.end
                ? t('shifts.master.durationValue', {
                    hours: duration.hours,
                    minutes: duration.minutes,
                  })
                : '—'}
            </Num>
          </div>
          <Num className="text-body-sm text-ink-dim">
            {(form.start || '--:--') + ' → ' + (form.end || '--:--')}
          </Num>
        </div>

        {wraps ? (
          <p className="mt-3 mb-0 text-body-sm text-ink-dim">{t('shifts.master.wraps')}</p>
        ) : null}
      </Section>

      <Section icon="cal" title={t('shifts.master.days')} sub={t('shifts.master.daysSub')}>
        <div className="mb-2.5 flex flex-wrap gap-1.5">
          <Chip onClick={() => set('days', [...DOW_ORDER])}>{t('shifts.master.dayAll')}</Chip>
          <Chip onClick={() => set('days', [1, 2, 3, 4, 5])}>
            {t('shifts.master.dayWeekdays')}
          </Chip>
          <Chip onClick={() => set('days', [])}>{t('shifts.master.dayClear')}</Chip>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {DOW_ORDER.map((day) => (
            <Chip
              key={day}
              selected={form.days.includes(day)}
              onClick={() =>
                set(
                  'days',
                  form.days.includes(day)
                    ? form.days.filter((entry) => entry !== day)
                    : [...form.days, day],
                )
              }
              className="min-w-11 justify-center"
            >
              {t(`shifts.master.dow.${day}`)}
            </Chip>
          ))}
        </div>
      </Section>

      <Section icon="factory" title={t('shifts.master.scope')} sub={t('shifts.master.scopeSub')}>
        {/* ONE plant, or the account. This was a checkbox list over `plant_ids`
            until the `shift_scopes` table was dropped — a pattern can no longer
            name three plants, so a list would be offering something the API has
            no way to store.

            ── Locked on edit, and that is the API's rule ─────────────────
            `plant_id` is Create-only: `ShiftUpdate` does not declare it, and the
            API 400s on fields a schema does not declare. So moving a pattern
            between plants is not an edit that exists. The control is disabled
            with the reason said rather than removed, because "which plant is
            this for" is still the question a reader has when they open the
            drawer.

            ── "Every plant" needs an account-level grant ─────────────────
            A plant-level manager creating one would be a 403, so the option is
            disabled for them rather than offered and refused. */}
        <Eyebrow as="p" className="mb-2">
          {t('shifts.master.field.site')}
        </Eyebrow>

        {isNew ? (
          <>
            <label className="flex cursor-pointer items-start gap-2 py-1 text-body-sm">
              <input
                type="radio"
                name="pattern-scope"
                checked={!oneSite}
                disabled={!grant.accountLevel}
                onChange={() => {
                  setOneSite(false)
                  set('plantId', null)
                }}
                className="mt-0.5 size-4 accent-[var(--hue-forge)]"
              />
              <span className="flex flex-col">
                <span className={grant.accountLevel ? undefined : 'text-ink-mute'}>
                  {t('shifts.master.allSitesScope')}
                </span>
                <span className="text-micro text-ink-mute">
                  {grant.accountLevel
                    ? t('shifts.master.allSitesHint')
                    : t('shifts.master.allSitesNeedsAccount')}
                </span>
              </span>
            </label>

            <label className="flex cursor-pointer items-start gap-2 py-1 text-body-sm">
              <input
                type="radio"
                name="pattern-scope"
                checked={oneSite}
                onChange={() => setOneSite(true)}
                className="mt-0.5 size-4 accent-[var(--hue-forge)]"
              />
              <span>{t('shifts.master.oneSiteScope')}</span>
            </label>

            {/* The shared "choose a place" dialog, limited to plants — it
                replaced a radio list of the first two hundred. */}
            {oneSite ? (
              <PlaceField
                className="mt-1 ms-6"
                label={t('shifts.master.field.site')}
                required
                value={chosenPlant}
                allowTiers={SITE_ONLY}
                placeholder={t('shifts.master.sitePlaceholder')}
                onChange={(place) => {
                  setPickedName(place?.name ?? null)
                  set('plantId', place?.id ?? null)
                }}
                error={plantNotGranted ? t('shifts.master.error.siteNotGranted') : undefined}
              />
            ) : null}
          </>
        ) : (
          <div className="rounded-sm border border-hairline bg-sunken px-3 py-2.5">
            <p className="m-0 text-body-sm text-ink">
              {form.plantId === null
                ? t('shifts.master.allSitesScope')
                : (siteName.get(form.plantId) ?? form.plantId)}
            </p>
            <p className="mt-1 mb-0 text-micro text-ink-mute">
              {t('shifts.master.siteFixed')}
            </p>
          </div>
        )}

        {/* The status is not a field here any more.
            ── Why it left the form ──────────────────────────────────────
            It was a checkbox among the other fields, which made turning a shift
            off look like editing one — you ticked it and then had to remember to
            press Save, and until you did the card still said the shift was on.
            Retiring a pattern is not a property of it; it is an act, with the
            same weight as deleting one and the same need to be sure.

            So it is a button beside Delete in the footer, behind a confirmation,
            applied on its own. `ShiftCreate` has no `is_active` either way, so
            there is nothing to offer on an add. */}

        {/* The timezone field and the grace select are commented out.

            Timezone was read from a fixture map keyed by site id; the hierarchy
            serves a plant's `timezone` on its own record but the tree does not
            carry it, so showing one here would mean a request per site to fill a
            read-only box.

            Grace is the one that costs something. It is the whole auto-detection
            story the banner tells — a login a minute before six is the six
            o'clock shift — and there is no column for it, so resolution runs at
            zero. See BACKEND-GAPS.md.
            <Field label={t('shifts.master.field.timeZone')} readOnly
                   value={SITE_TIME_ZONE[form.site] ?? '—'} />
            <ForgeSelect label={t('shifts.master.field.grace')} value={String(form.grace)}
                         options={GRACE_OPTIONS.map(…)} />
            <p>{t('shifts.master.graceHint')}</p> */}
      </Section>

      {conflicts.length ? (
        <p className="mb-3 rounded-sm border border-hairline tint-amber px-3.5 py-2.5 text-body-sm text-on-amber">
          {t('shifts.master.conflict', {
            names: conflicts
              .map((entry) => `${entry.name} (${entry.start}–${entry.end})`)
              .join(', '),
          })}
        </p>
      ) : null}

      {error ? (
        <p className="m-0 rounded-sm border border-hairline tint-red px-3.5 py-2.5 text-body-sm text-on-red">
          {error}
        </p>
      ) : null}
    </ForgeDrawer>
  )
}

function Section({ icon, title, sub, children }) {
  return (
    <section className="mb-4 rounded-sm border border-hairline bg-card p-4">
      <div className="mb-3 flex items-center gap-2.5">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-xs bg-sunken">
          <Icon name={icon} size="xs" className="text-ink-mute" />
        </span>
        <div className="min-w-0">
          <h3 className="m-0 text-body-sm font-semibold text-ink">{title}</h3>
          <p className="m-0 text-micro text-ink-mute">{sub}</p>
        </div>
      </div>
      {children}
    </section>
  )
}

const SITE_ONLY = new Set(['site'])
